import { useTheme } from '@emotion/react';
import { Formik } from 'formik';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  EntryList,
  Grid,
  Header,
  Loader,
  Radio,
  RadioGroup,
} from '~/components/blocks';
import { Drawer } from '~/components/blocks/Drawer';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { settingsDrawerState } from '~/state/settings_systemsettings/atoms';

import { PermissionError } from '../PermissionError';
import { DownloadPoster } from './DownloadPoster';
import { Fields } from './types';
import { useCheckin } from './use-checkin';
import { validationSchema } from './validation';

export const CheckinSettingDrawer = () => {
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);
  const { verifyRole } = usePractitionerRole();
  const enabledEdit = verifyRole(['admin', 'company_admin']);
  const state = useAtomValue(settingsDrawerState);
  const reset = useResetAtom(settingsDrawerState);
  const isOpen = state.drawer === 'checkin';
  const {
    loading,
    updating,
    isActive,
    checkinOrganizationId,
    refetchCheckinSetting,
    updateCheckinSetting,
  } = useCheckin();
  const initialValues = useMemo<Fields>(
    () => ({
      enable: isActive ? 'enable' : 'disable',
    }),
    [isActive],
  );

  const close = useCallback(() => {
    reset();
  }, [reset]);

  const handleSubmit = useCallback(
    async (values: Fields) => {
      try {
        await updateCheckinSetting({
          variables: {
            input: {
              active: values.enable === 'enable',
            },
          },
        });
        close();
      } catch (error) {
        setError(error.message || 'エラーが発生しました');
      }
    },
    [close, updateCheckinSetting],
  );

  useEffect(() => {
    if (isOpen) {
      refetchCheckinSetting();
    } else {
      setError(null);
    }
  }, [refetchCheckinSetting, isOpen]);

  return (
    <Drawer open={isOpen} onClose={close}>
      <Grid height="100%" gridTemplateRows="min-content 1fr min-content">
        <Header attached="drawer">店舗チェックイン設定</Header>
        <Formik
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {(formik) =>
            loading ? (
              <Loader open inside />
            ) : (
              <>
                <Box paddingX={theme.space.xl}>
                  {error && <Alert mb={theme.space.m}>{error}</Alert>}
                  <EntryList>
                    <EntryList.Head>ステータス</EntryList.Head>
                    <EntryList.Body>
                      <RadioGroup>
                        <Radio
                          name="enable"
                          label="有効"
                          value="enable"
                          checked={formik.values.enable === 'enable'}
                          onChange={formik.handleChange}
                          disabled={updating || !enabledEdit}
                        />
                        <Radio
                          name="enable"
                          label="無効"
                          value="disable"
                          checked={formik.values.enable === 'disable'}
                          onChange={formik.handleChange}
                          disabled={updating || !enabledEdit}
                        />
                      </RadioGroup>
                    </EntryList.Body>
                  </EntryList>
                  <DownloadPoster
                    disabled={!isActive}
                    checkinOrganizationId={checkinOrganizationId}
                  />
                </Box>
                <Drawer.Footer>
                  {!enabledEdit && <PermissionError />}
                  <Button
                    type="button"
                    wide="fill"
                    use="base"
                    size="l"
                    loading={updating}
                    disabled={!enabledEdit}
                    onClick={formik.submitForm}
                  >
                    保存
                  </Button>
                </Drawer.Footer>
              </>
            )
          }
        </Formik>
      </Grid>
    </Drawer>
  );
};
